import { getCalendarItemCoords } from '$tablet-app/src/app/calendar/common/actions';
import NewButton from '$tablet-app/src/app/components/Button/NewButton';
import CalendarItemCheckbox from '$tablet-app/src/app/components/CalendarItemCheckbox/CalendarItemCheckbox';
import CalendarItemDetailsGoalSection from '$tablet-app/src/app/components/CalendarItemDetail/CalendarItemDetailsGoalSection';
import { EmojiWithBackground } from '$tablet-app/src/app/components/EmojiWithBackground/EmojiWithBackground';
import RecipePanel from '$tablet-app/src/app/components/RecipePanel';
import Row from '$tablet-app/src/app/components/Row';
import Spacer from '$tablet-app/src/app/components/Spacer';
import Text from '$tablet-app/src/app/components/Text';
import MapView from '$tablet-app/src/app/maps/MapView';
import { Recipe } from '$tablet-app/src/app/recipes/types';
import { getStartDate } from '@akin-home/calendar';
import { CalendarItemUnion } from '@akin-home/calendar/combined';
import { CheckListItem } from '@akin-home/checklists';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { format } from 'date-fns';
import React, { FC } from 'react';
import { View } from 'react-native';
import styled from 'styled-components/native';

interface Props {
  item: CalendarItemUnion;
  recipes?: Recipe[];

  onPressEdit?: (item: CalendarItemUnion) => void;
  onPressDelete?: (item: CalendarItemUnion) => void;
  onPressRecipe?: (recipe: Recipe) => void;
  onChecklistPress?: (
    calendarItem: CalendarItemUnion,
    item: CheckListItem
  ) => void;
}

export const CalendarItemDetailsBody = (props: Props) => {
  const {
    item,
    recipes,
    onPressEdit,
    onPressDelete,
    onPressRecipe,
    onChecklistPress,
  } = props;

  const start = getStartDate(item);

  const coords = getCalendarItemCoords(item);
  const showChecklist = item.type !== 'Meal' && item.checklist?.length;

  const handleEditPress = () => onPressEdit?.(item);
  const handleDeletePress = () => onPressDelete?.(item);

  return (
    <Container>
      <Header>
        <Row justifyContent="space-between" alignItems="center">
          {item.emoji ? (
            <Emoji emoji={item.emoji} emojiStyle={EMOJI_STYLE} />
          ) : (
            <View />
          )}

          <ActionButtonRow>
            {item.type !== 'Meal' ? (
              <ActionButton
                icon="pencil"
                variant="text"
                onPress={handleEditPress}
                testID="edit-calendar-item-button"
              >
                Change
              </ActionButton>
            ) : null}

            <ActionButton
              icon="trash"
              variant="text"
              onPress={handleDeletePress}
              testID="delete-calendar-item-button"
            >
              Delete
            </ActionButton>
          </ActionButtonRow>
        </Row>

        <Text variant="h2" weight="semibold">
          {item.title}
        </Text>

        <Text>
          {format(start, 'EEEE, d MMMM')} {format(start, 'p')}{' '}
          {'end' in item && item.end ? ` - ${format(item.end, 'p')}` : ''}
        </Text>
      </Header>

      <View>
        {recipes
          ? recipes.map((recipe) => (
              <RecipePanel
                key={recipe.id}
                recipe={recipe}
                onClick={() => onPressRecipe?.(recipe)}
              />
            ))
          : null}

        {showChecklist ? (
          <RowWithIcon icon="tasks">
            <View>
              <Text weight="semibold">What do you need to do or bring?</Text>
              <Spacer height="10px" />
              {'checklist' in item
                ? item.checklist?.map((listItem) => (
                    <View key={listItem.id}>
                      <CalendarItemCheckbox
                        calendarItem={item}
                        checklistItem={listItem}
                        onChange={onChecklistPress}
                        fieldName="checklist"
                      />
                      <Spacer height="10px" />
                    </View>
                  ))
                : null}
            </View>
          </RowWithIcon>
        ) : null}

        {!coords && item.notes ? (
          <RowWithIcon icon="sticky-note">
            <View>
              <Text preset="bold">Note</Text>
              <Text>{item.notes}</Text>
            </View>
          </RowWithIcon>
        ) : null}

        {coords ? (
          <MapContainer>
            <MapView {...coords} />
          </MapContainer>
        ) : null}

        {item.type === 'Alert' && item.goalID ? (
          <GoalSectionWrapper>
            <CalendarItemDetailsGoalSection
              goal_id={item.goalID}
              date={start}
            />
          </GoalSectionWrapper>
        ) : null}
      </View>
    </Container>
  );
};

const RowWithIcon: FC<{
  icon: IconProp;
  children: React.ReactNode;
}> = (props) => (
  <Row>
    <RowIcon icon={props.icon} color="grey" />
    <RowContent>{props.children}</RowContent>
  </Row>
);

const ActionButtonRow = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  flex: 1;
  justify-content: flex-end;
`;

const Container = styled.View`
  padding: 32px;
  gap: 10px;
`;

const Header = styled.View`
  gap: 4px;
`;

const RowIcon = styled(FontAwesomeIcon)`
  margin: 5px 15px 0 0;
`;

const RowContent = styled.View`
  flex: 1;
`;

const ActionButton = styled(NewButton)`
  opacity: 0.6;
`;

const Emoji = styled(EmojiWithBackground)`
  height: 66px;
  width: 66px;
`;

const GoalSectionWrapper = styled.View`
  flex-direction: row;
  justify-content: center;
`;

const MapContainer = styled.View`
  width: 400px;
  height: 300px;
`;

const EMOJI_STYLE = { fontSize: 30 };
